import type { ExpandedIndex, useDisclosure } from '@chakra-ui/react'
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useControllableState,
  Button,
  HStack,
  VStack,
  ModalFooter,
  Box,
} from '@chakra-ui/react'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, Field, Formik } from 'formik'
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js'
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
} from '@chakra-ui/form-control'
import useFetch, { CachePolicies } from 'use-http'
import * as Yup from 'yup'
import type { UserSessionResponse, BillingDetails } from '../../../interfaces'
import { SelectField, TextField } from '../../Form'
import { allowedBusinessCountries } from '../../../../utils/countries'
import { usStatesCodeMap } from '../../../../utils/USStates'
import useToast from '../../../hooks/useToast'

type Props = Pick<ReturnType<typeof useDisclosure>, 'isOpen' | 'onClose'> & {
  user: UserSessionResponse
}

const PlanUpgradeModal = ({
  isOpen,
  onClose,
  user,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()

  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const [index, setIndex] = useControllableState<ExpandedIndex>({
    defaultValue: 0,
  })

  const stripe = useStripe()
  const elements = useElements()

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose()
      }}
      size="xl"
    >
      <ModalOverlay />
      <ModalContent>
        <Formik<BillingDetails>
          onSubmit={async (values, actions) => {
            const data = (await request.post(
              '/api/billing/details',
              values,
            )) as {
              _id: string
            }

            if (elements && stripe) {
              const card = elements.getElement(CardElement)
              if (card) {
                const { error, token } = await stripe.createToken(card)
                if (error) {
                  actions.setErrors({ token: error.message })
                  return
                }

                if (!token) {
                  actions.setErrors({
                    token: 'Invalid card details.',
                  })
                  return
                }

                const response = await request.post(
                  `/api/billing/upgrade/${data._id}`,
                  {
                    token: token.id,
                    clientNumber: user.clientNumber,
                  },
                )

                if (request.error) {
                  let message = 'Please contact support@imagekit.io.'
                  if (response?.message) {
                    message = response.message
                  }
                  toast({
                    title: t(
                      'subscription_payment_could_not_be_verified',
                      'Subscription payment could not be verified!',
                    ),
                    description: message,
                    status: 'error',
                    isClosable: true,
                    duration: 5000,
                    position: 'top-right',
                    variant: 'outline',
                  })
                  return
                }

                toast({
                  description: t(
                    'account_upgraded_success',
                    'Account upgraded. Taking you to subscription page.',
                  ),
                  status: 'success',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })

                window.trackEvent({ label: 'UpgradedPlan' })
                onClose()

                setTimeout(() => {
                  window.location.href = '/dashboard/billing/subscriptions'
                }, 3000)
              }
            }
          }}
          initialValues={{
            clientNumber: user.clientNumber,
            company_name: user.billingDetails.company_name ?? '',
            billing_email:
              user.billingDetails.contact_persons?.[0]?.email ?? '',
            address: user.billingDetails.billing_address?.address ?? '',
            city: user.billingDetails.billing_address?.city ?? '',
            state: user.billingDetails.billing_address?.state ?? '',
            country: user.billingDetails.billing_address?.country ?? '',
            zip: user.billingDetails.billing_address?.zip ?? '',
            gstin: user.billingDetails.gstin ?? '',
            token: '',
          }}
          validationSchema={Yup.object().shape({
            clientNumber: Yup.string().required(t('required', 'Required')),
            company_name: Yup.string().required(t('required', 'Required')),
            billing_email: Yup.string().required(t('required', 'Required')),
            address: Yup.string().required(t('required', 'Required')),
            city: Yup.string().required(t('required', 'Required')),
            state: Yup.string().required(t('required', 'Required')),
            country: Yup.string().required(t('required', 'Required')),
            zip: Yup.string().required(t('required', 'Required')),
          })}
        >
          {({ values, isSubmitting }) => (
            <Form>
              <ModalHeader>{t('upgrade_plan', 'Upgrade plan')}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack spacing={10} mt={4} alignItems="stretch">
                  <TextField<BillingDetails>
                    name="company_name"
                    label={t('company_name', 'Company Name')}
                    required={true}
                  />
                  <TextField<BillingDetails>
                    name="billing_email"
                    label={t('billing_email', 'Billing Email')}
                    required={true}
                  />
                  <TextField<BillingDetails>
                    name="address"
                    label={t('address', 'Address')}
                    required={true}
                  />
                  <HStack alignItems="flex-start">
                    <SelectField<BillingDetails>
                      name="country"
                      label={t('country', 'Country')}
                      options={allowedBusinessCountries.map(x => ({
                        label: x,
                        value: x,
                      }))}
                      required={true}
                    />
                    {values.country === 'United States' ? (
                      <SelectField<BillingDetails>
                        name="state"
                        label={t('state', 'State')}
                        required={true}
                        options={Object.entries(usStatesCodeMap).map(
                          ([value, label]) => ({ value, label }),
                        )}
                      />
                    ) : (
                      <TextField<BillingDetails>
                        name="state"
                        label={t('state', 'State')}
                        required={true}
                      />
                    )}
                  </HStack>
                  <HStack alignItems="flex-start">
                    <TextField<BillingDetails>
                      name="city"
                      label={t('city', 'City')}
                      required={true}
                    />
                    <TextField<BillingDetails>
                      name="zip"
                      label={t('zip_code', 'Zip Code')}
                      required={true}
                    />
                  </HStack>
                  <TextField<BillingDetails>
                    name="gstin"
                    label={t('gst_vat_number', 'GST/VAT Number')}
                  />
                  <Field name="token">
                    {({ field, form }) => (
                      <FormControl
                        name={field.name}
                        isInvalid={Boolean(
                          form.errors[field.name] && form.touched[field.name],
                        )}
                      >
                        <FormLabel>
                          {t('card_details', 'Card details')}
                        </FormLabel>
                        <Box
                          borderRadius="base"
                          borderWidth="1px"
                          borderColor="#e6e7ea"
                          padding="6.9px 17px"
                        >
                          <CardElement
                            options={{
                              style: {
                                base: {
                                  fontSize: '15px',
                                  color: '#32325d',
                                },
                              },
                            }}
                          />
                        </Box>
                        <FormErrorMessage marginBottom="-2.0408em">
                          {form.errors.token}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </VStack>
              </ModalBody>
              <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
                <Button type="submit" isLoading={isSubmitting}>
                  {t('upgrade', 'Upgrade')}
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default PlanUpgradeModal
